import type {AppDispatch} from '../../../store/store';
import {usersApi, type User} from '../../../store/features/Users/userService';
import logger from '../../../services/logger';
import {blockCallDuringGroupCall} from '../../../services/groupCallBusyGuard';
import {
    answerMatrixCall,
    endMatrixCall,
    makeMatrixCall,
    rejectMatrixCall,
    sendMatrixCallDtmf,
    setMatrixCallCamera,
    setMatrixCallHold,
    setMatrixCallOutputDevice,
    setMatrixCallScreenShare,
    switchMatrixCallDevice,
    toggleMatrixCallMute,
} from '../../../services/matrixCallService';

export interface InternalCallee {
    readonly userId: string | number;
    readonly firstName?: string;
    readonly lastName?: string;
}

const run = (control: string, action: () => Promise<unknown>): void => {
    void action().catch(error =>
        logger.warn('Direct call control failed', {
            source: 'call_video',
            event: 'control_failed',
            control,
            error: error instanceof Error ? error.message : String(error),
        }),
    );
};

export const matrixActions = {
    answer: (callId: string, video: boolean, dispatch: AppDispatch) => run('answer', () => answerMatrixCall(callId, dispatch, {video})),
    decline: (callId: string, dispatch: AppDispatch) => run('decline', () => rejectMatrixCall(callId, dispatch)),
    end: (callId: string, dispatch: AppDispatch) => run('end', () => endMatrixCall(callId, dispatch)),
    toggleMute: () => run('microphone', () => toggleMatrixCallMute()),
    setHold: (held: boolean) => setMatrixCallHold(held),
    dtmf: (digit: string) => sendMatrixCallDtmf(digit),
    setCamera: (enabled: boolean) => run('camera', () => setMatrixCallCamera(enabled)),
    setScreenShare: (enabled: boolean) => run('screen_share', () => setMatrixCallScreenShare(enabled)),
    switchDevice: (kind: MediaDeviceKind, deviceId: string) =>
        run('switch_device', () => (kind === 'audiooutput' ? setMatrixCallOutputDevice(deviceId) : switchMatrixCallDevice(kind, deviceId))),
};

interface StartInternalCallOptions {
    readonly video?: boolean;
    readonly roomId?: string;
    readonly callState: unknown;
    readonly currentUserId: string;
    readonly tenant: string;
    readonly dispatch: AppDispatch;
}

const MATRIX_ID = /^@.+:.+$/;

const resolvePhoneHqUser = async (callee: InternalCallee, dispatch: AppDispatch): Promise<InternalCallee> => {
    if (typeof callee.userId !== 'string' || !MATRIX_ID.test(callee.userId)) {
        return callee;
    }
    const matrixId = callee.userId;
    const resolved = await dispatch(usersApi.endpoints.resolveMatrixIds.initiate({matrixIds: [matrixId]})).unwrap();
    const userId = resolved[matrixId];
    if (userId === undefined || userId === null) {
        throw new Error('Caller could not be resolved to a PhoneHQ user');
    }
    return {...callee, userId};
};

export const startInternalCall = async (callee: InternalCallee, options: StartInternalCallOptions): Promise<void> => {
    const {callState, dispatch} = options;
    if (blockCallDuringGroupCall(callState, dispatch) || callState !== 'IDLE') {
        return;
    }
    const resolved = await resolvePhoneHqUser(callee, dispatch);
    // A callback already knows exactly who called; never re-resolve that by display name.
    const peerMatrixId = typeof callee.userId === 'string' && MATRIX_ID.test(callee.userId) ? callee.userId : undefined;
    await makeMatrixCall(resolved as unknown as User, options.currentUserId, options.tenant, dispatch, options.roomId, {video: options.video, peerMatrixId});
};
